import { useIonToast } from '@ionic/react';
import { t } from '@lingui/macro';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useLocation } from 'react-router';
import * as Yup from 'yup';

import ICredentials from '../../../interfaces/ICredentials';
import { loginAtom } from '../../../store/Atoms';
import {
    LOGIN_DEV_PASSWORD,
    LOGIN_DEV_USERNAME,
} from '../../../utils/constants';
import { useCustomRouter } from '../../../utils/hooks/useCustomRouter';
import LoadingButton from '../../loadingbutton/LoadingButton';
import DefaultInput from '../defaultinput/DefaultInput';

const MIN_PASSWORD_LENGTH = 6;
const LoginSchema = () =>
    Yup.object().shape({
        username: Yup.string().required(t`Username is required`),
        password: Yup.string()
            .min(
                MIN_PASSWORD_LENGTH,
                t`Password is too short! Min ${MIN_PASSWORD_LENGTH} chars`
            )
            .required(t`Password is required`),
    });

const LoginForm = () => {
    const router = useCustomRouter();
    const [, loginAction] = useAtom(loginAtom);
    const [presentToast] = useIonToast();
    const initialFormValues: ICredentials = {
        username: LOGIN_DEV_USERNAME,
        password: LOGIN_DEV_PASSWORD,
    };
    const location = useLocation<{ from?: string }>();

    const handleFormSubmit = useCallback(
        async (
            credentials: ICredentials,
            actions: FormikHelpers<ICredentials>
        ) => {
            actions.setSubmitting(true);

            try {
                await loginAction(credentials);
                // Redirect the user back to the attempted URL if available, or to a default route
                const from = location.state?.from || '/';
                router.push(from, 'forward', 'pop');
            } catch (e) {
                await presentToast({
                    message: 'Invalid credentials',
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: 'Dismiss',
                            role: 'cancel',
                        },
                    ],
                });
            }

            actions.setSubmitting(false);
        },
        [location.state?.from, loginAction, presentToast, router]
    );

    return (
        <div className="ion-margin-top">
            <Formik
                initialValues={initialFormValues}
                validationSchema={LoginSchema()}
                validateOnMount={true}
                onSubmit={handleFormSubmit}
            >
                {({
                    values,
                    handleChange,
                    errors,
                    touched,
                    isValid,
                    isSubmitting,
                }) => (
                    <Form>
                        <DefaultInput
                            className={classNames({
                                'ion-invalid': errors.username,
                                'ion-touched': touched.username,
                            })}
                            name="username"
                            value={values.username}
                            label={t`Username`}
                            onIonInput={handleChange}
                            errorText={errors.username}
                            data-tiko-test="username-field"
                        ></DefaultInput>
                        <DefaultInput
                            className={classNames({
                                'ion-invalid': errors.password,
                                'ion-touched': touched.password,
                            })}
                            type="password"
                            name="password"
                            value={values.password}
                            label={t`Password`}
                            clearOnEdit={false}
                            onIonInput={handleChange}
                            errorText={errors.password}
                            data-tiko-test="password-field"
                        ></DefaultInput>
                        <LoadingButton
                            disabled={!isValid}
                            loading={isSubmitting}
                            className="ion-margin-top"
                            type="submit"
                            data-tiko-test="login-button"
                        >
                            Login
                        </LoadingButton>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default LoginForm;
